/**
 * SunnySign Mobile Debug Logger
 * Zeigt optional einen scrollbaren Log im Taskpane (Debugging, z. B. Mobile).
 * Panel standardmäßig ausgeblendet. Wieder einblenden:
 *   - hier DEBUG_PANEL_UI_OVERRIDE auf true setzen, oder
 *   - in der Taskpane-Konsole: localStorage.setItem("sunnysign_debug_panel", "1") dann Reload.
 * dbg*() schreibt weiterhin in die Browser-Konsole.
 */

export type LogLevel = "info" | "ok" | "warn" | "error" | "step";

export interface LogEntry {
  ts: string;
  level: LogLevel;
  msg: string;
}

const MAX_ENTRIES = 80;
const entries: LogEntry[] = [];
const listeners: Array<() => void> = [];

function timestamp(): string {
  const d = new Date();
  return [
    d.getHours().toString().padStart(2, "0"),
    d.getMinutes().toString().padStart(2, "0"),
    d.getSeconds().toString().padStart(2, "00"),
  ].join(":") + "." + d.getMilliseconds().toString().padStart(3, "0");
}

export function dbg(level: LogLevel, msg: string): void {
  entries.unshift({ ts: timestamp(), level, msg });
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
  const prefix = { info: "ℹ", ok: "✓", warn: "⚠", error: "✗", step: "→" }[level];
  console.log(`[SunnySign Debug][${level.toUpperCase()}] ${msg}`);
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

export const dbgInfo  = (msg: string) => dbg("info",  msg);
export const dbgOk    = (msg: string) => dbg("ok",    msg);
export const dbgWarn  = (msg: string) => dbg("warn",  msg);
export const dbgError = (msg: string) => dbg("error", msg);
export const dbgStep  = (msg: string) => dbg("step",  msg);

export function getEntries(): LogEntry[] { return [...entries]; }

export function clearEntries(): void {
  entries.length = 0;
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
}

export function onUpdate(fn: () => void): () => void {
  listeners.push(fn);
  return () => {
    const i = listeners.indexOf(fn);
    if (i >= 0) listeners.splice(i, 1);
  };
}

/** Gleiche Origin: Autorun-WebView (compose) → Taskpane-Debug-Panel */
const AUTORUN_BC = "sunnysign-autorun-debug-v1";
let autorunBcListener: BroadcastChannel | null = null;

export function subscribeAutorunDebugFromCompose(): void {
  if (typeof BroadcastChannel === "undefined" || autorunBcListener) return;
  try {
    autorunBcListener = new BroadcastChannel(AUTORUN_BC);
    autorunBcListener.onmessage = (ev: MessageEvent<{ text?: string; level?: LogLevel }>) => {
      const t = ev.data?.text;
      if (!t) return;
      const level = ev.data?.level ?? "step";
      dbg(level, t);
    };
  } catch {
    /* ignore */
  }
}

export function broadcastAutorunDebug(text: string, level: LogLevel = "step"): void {
  if (typeof BroadcastChannel === "undefined") return;
  try {
    const ch = new BroadcastChannel(AUTORUN_BC);
    ch.postMessage({ text, level });
    ch.close();
  } catch {
    /* ignore */
  }
}

// ─── DOM-Panel ───────────────────────────────────────────────────────────────

const PANEL_ID = "sunnysign-debug-panel";
const DEBUG_PANEL_LS_KEY = "sunnysign_debug_panel";

/** Sofort wieder sichtbar: auf `true` setzen (Build) oder localStorage-Key siehe Dateikopf. */
const DEBUG_PANEL_UI_OVERRIDE = false;

export function isDebugPanelUiEnabled(): boolean {
  if (DEBUG_PANEL_UI_OVERRIDE) return true;
  try {
    if (typeof localStorage !== "undefined" && localStorage.getItem(DEBUG_PANEL_LS_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  return false;
}

export function mountDebugPanel(): void {
  if (!isDebugPanelUiEnabled()) return;
  if (typeof document === "undefined") return;
  if (document.getElementById(PANEL_ID)) return;

  const panel = document.createElement("div");
  panel.id = PANEL_ID;
  panel.innerHTML = `
    <div class="ss-dbg__header">
      <span class="ss-dbg__title">🐛 Debug Log</span>
      <button type="button" class="ss-dbg__clear" id="ss-dbg-clear">Leeren</button>
      <button type="button" class="ss-dbg__toggle" id="ss-dbg-toggle">▼</button>
    </div>
    <div class="ss-dbg__body" id="ss-dbg-body"></div>
  `;

  // Inline-Styles damit kein externes CSS nötig ist
  Object.assign(panel.style, {
    position: "fixed",
    bottom: "0",
    left: "0",
    right: "0",
    zIndex: "99999",
    fontFamily: "monospace",
    fontSize: "10px",
    background: "#0a0a0a",
    color: "#e0e0e0",
    borderTop: "2px solid #f7ba05",
    maxHeight: "40vh",
    display: "flex",
    flexDirection: "column",
  });

  document.body.appendChild(panel);

  const bodyEl = document.getElementById("ss-dbg-body")!;
  Object.assign(bodyEl.style, {
    overflowY: "auto",
    flex: "1",
    padding: "4px 6px",
  });

  const headerEl = panel.querySelector(".ss-dbg__header") as HTMLElement;
  Object.assign(headerEl.style, {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "4px 6px",
    background: "#1a1a1a",
    borderBottom: "1px solid #333",
    flexShrink: "0",
  });

  const titleEl = panel.querySelector(".ss-dbg__title") as HTMLElement;
  Object.assign(titleEl.style, { flex: "1", fontWeight: "bold", color: "#f7ba05" });

  [".ss-dbg__clear", ".ss-dbg__toggle"].forEach((sel) => {
    const btn = panel.querySelector(sel) as HTMLElement;
    Object.assign(btn.style, {
      background: "#333",
      border: "none",
      color: "#ccc",
      padding: "2px 6px",
      borderRadius: "4px",
      cursor: "pointer",
      fontSize: "10px",
    });
  });

  let collapsed = false;
  document.getElementById("ss-dbg-toggle")!.addEventListener("click", () => {
    collapsed = !collapsed;
    bodyEl.style.display = collapsed ? "none" : "";
    (document.getElementById("ss-dbg-toggle") as HTMLButtonElement).textContent = collapsed ? "▲" : "▼";
  });

  document.getElementById("ss-dbg-clear")!.addEventListener("click", () => {
    clearEntries();
    renderEntries();
  });

  const levelColor: Record<LogLevel, string> = {
    info:  "#8be9fd",
    ok:    "#50fa7b",
    warn:  "#ffb86c",
    error: "#ff5555",
    step:  "#bd93f9",
  };

  function renderEntries(): void {
    const all = getEntries();
    bodyEl.innerHTML = all
      .map(
        (e) =>
          `<div style="padding:1px 0;border-bottom:1px solid #1a1a1a;">` +
          `<span style="color:#555">${e.ts}</span> ` +
          `<span style="color:${levelColor[e.level]};font-weight:bold">[${e.level.toUpperCase()}]</span> ` +
          `<span>${e.msg.replace(/</g, "&lt;")}</span>` +
          `</div>`
      )
      .join("");
    // Neueste oben → kein Scrollen nötig
  }

  onUpdate(renderEntries);
  renderEntries();

  dbgInfo("Debug-Panel gestartet");
}
